import type { AdapterDeltaOutputField, AdapterMappingDefinition, AdapterMappingGuidance } from "./types";

/** The JSON-e key whose template runs once per source record, with that record bound to `record`. */
export const DELTA_EACH_RECORD_KEY = "each(record)";

/** Where an output field's value lives in a delta mapping's `data` template. */
export const getDeltaOutputFieldPath = (key: string): string => `data.${DELTA_EACH_RECORD_KEY}.${key}`;

const OUTPUT_FIELD_PATH_PREFIX = `data.${DELTA_EACH_RECORD_KEY}.`;

/** The output field key a guidance path points at, e.g. `employeeId` for `data.each(record).employeeId`. */
export const getDeltaOutputFieldKey = (path: string): string | undefined =>
  path.startsWith(OUTPUT_FIELD_PATH_PREFIX) ? path.slice(OUTPUT_FIELD_PATH_PREFIX.length) : undefined;

const escapeField = (field: string): string => field.replace(/\\/g, "\\\\").replace(/'/g, "\\'");

/** The template value that copies one source `record` field into an output field. */
export const formatRecordFieldReference = (field: string): { $eval: string } => ({
  $eval: `record['${escapeField(field)}']`,
});

const bracketReferencePattern = /^\s*record\s*\[\s*(['"])((?:\\.|(?!\1).)*)\1\s*\]\s*$/;
const dotReferencePattern = /^\s*record\s*\.\s*([A-Za-z_$][\w$]*)\s*$/;

/**
 * The `record` field an output value copies, when it is exactly `{ "$eval": "record['X']" }` (or `record["X"]`,
 * `record.X`, or that expression as a plain string). Returns `""` for an unfilled `record['']` placeholder and
 * `undefined` for anything else, such as a computed expression.
 */
export const parseRecordFieldReference = (value: unknown): string | undefined => {
  const expression =
    typeof value === "string"
      ? value
      : value && typeof value === "object" && !Array.isArray(value) && Object.keys(value).length === 1
        ? (value as { $eval?: unknown }).$eval
        : undefined;
  if (typeof expression !== "string") return undefined;

  const bracket = expression.match(bracketReferencePattern);
  if (bracket) return bracket[2].replace(/\\(.)/g, "$1");
  const dot = expression.match(dotReferencePattern);
  return dot ? dot[1] : undefined;
};

/**
 * Whether an output value is filled: a reference to a non-empty `record` field (one of `recordFields`, when
 * they are known), or any other non-empty expression, which is trusted as written.
 */
export const isRecordFieldValueComplete = (value: unknown, recordFields?: string[]): boolean => {
  const field = parseRecordFieldReference(value);
  if (field === undefined) {
    if (typeof value === "string") return value.trim().length > 0;
    if (value && typeof value === "object") return Object.keys(value).length > 0;
    return typeof value === "number" || typeof value === "boolean";
  }
  if (field.trim() === "") return false;
  return !recordFields || recordFields.length === 0 || recordFields.includes(field.trim());
};

/** `$map` over the source records, with every output field waiting for its `record` field. */
export const buildDeltaDataTemplate = (fields: AdapterDeltaOutputField[]): Record<string, unknown> => ({
  $map: { $eval: "data" },
  [DELTA_EACH_RECORD_KEY]: Object.fromEntries(fields.map((field) => [field.key, formatRecordFieldReference("")])),
});

export const getDeltaOutputFieldGuidanceId = (mappingKey: string, fieldKey: string): string =>
  `${mappingKey}-output-${fieldKey}`;

/** One guided field per output field, each checking it copies a `record` field. */
export const getDeltaOutputFieldGuidance = (
  mappingKey: string,
  fields: AdapterDeltaOutputField[],
): AdapterMappingGuidance[] =>
  fields.map((field) => {
    const path = getDeltaOutputFieldPath(field.key);
    return {
      id: getDeltaOutputFieldGuidanceId(mappingKey, field.key),
      label: field.label,
      targetPath: path,
      instructions: field.instructions,
      dataType: field.dataType,
      exampleValue: field.exampleValue,
      optional: !field.required,
      requirements: [{ path, operator: "recordField" as const }],
    };
  });

/**
 * Completes a delta mapping definition from its `outputFields`: the `data` template skeleton (unless it already
 * has one) and a guided field for each output field, after the definition's own guidance.
 */
export const withDeltaOutputFields = (mapping: AdapterMappingDefinition): AdapterMappingDefinition => {
  const fields = mapping.outputFields ?? [];
  if (fields.length === 0) return mapping;

  const data = (mapping.data && typeof mapping.data === "object" ? mapping.data : {}) as Record<string, unknown>;
  const template = data.data && typeof data.data === "object" && Object.keys(data.data).length > 0
    ? data.data
    : buildDeltaDataTemplate(fields);

  return {
    ...mapping,
    data: { ...data, data: template },
    guidance: [...(mapping.guidance ?? []), ...getDeltaOutputFieldGuidance(mapping.key, fields)],
  };
};

/** The output fields an editor should offer, read back from a mapping's guidance. */
export const getDeltaOutputFieldsFromGuidance = (
  guidance: AdapterMappingGuidance[],
): Array<{ key: string; guidance: AdapterMappingGuidance }> =>
  guidance.flatMap((item) => {
    const key = getDeltaOutputFieldKey(item.targetPath);
    return key && item.requirements.some((requirement) => requirement.operator === "recordField")
      ? [{ key, guidance: item }]
      : [];
  });
